import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

/**
 * BoxLabel — 4 inch × 6 inch shipping label, optimised for roll printers.
 * CSS targets @page size 4in 6in.
 */
export default function BoxLabel() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ company_name: "US Operations" });

  useEffect(() => {
    (async () => {
      const [b, s] = await Promise.all([api.get(`/trace/box/${id}`), api.get("/settings")]);
      setData(b.data);
      setSettings(s.data);
    })();
  }, [id]);

  if (!data) return <div className="font-display uppercase">Loading...</div>;
  const { box } = data;
  const traceUrl = `${window.location.origin}/trace?box=${box.box_number}`;

  return (
    <div data-testid="box-label-page">
      <style>{`
        @page { size: 4in 6in; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .label-wrap { margin: 0 !important; }
          .no-print { display: none !important; }
        }
        .label-4x6 {
          width: 4in; height: 6in;
          padding: 0.25in;
          background: #fff;
          color: #1e293b;
          font-family: 'IBM Plex Mono', monospace;
          box-sizing: border-box;
          border: 2px solid #1e293b;
          display: flex; flex-direction: column;
        }
      `}</style>

      <div className="flex items-center justify-between p-4 no-print">
        <Link to={`/boxes/${id}`} className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back to Box
        </Link>
        <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="print-label-btn">
          <Printer size={14} weight="bold" /> Print 4×6 Label
        </button>
      </div>

      <div className="flex justify-center p-4 label-wrap">
        <div className="label-4x6">
          {/* Header */}
          <div style={{ borderBottom: "2px solid #1e293b", paddingBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
            <img src="/logo-gate.jpg" alt="GATE" style={{ width: 44, height: 44, border: "1.5px solid #334155", objectFit: "cover" }} />
            <div>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11, letterSpacing: 2, color: "#1d4ed8" }}>// {settings.company_name?.toUpperCase()}</div>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 22, lineHeight: 1, marginTop: 2 }}>SHIPPING LABEL</div>
            </div>
          </div>

          {/* Box # large */}
          <div style={{ marginTop: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#52525b" }}>Box Number</div>
            <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 36, letterSpacing: -1, lineHeight: 1 }}>{box.box_number}</div>
          </div>

          {/* Product */}
          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
            <div style={{ border: "1.5px solid #1e293b", padding: 6 }}>
              <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: "#52525b" }}>Product</div>
              <div style={{ fontWeight: 800 }}>{box.product_part_number || "—"}</div>
              <div style={{ fontSize: 9, lineHeight: 1.2 }}>{box.product_name}</div>
            </div>
            <div style={{ border: "1.5px solid #1e293b", padding: 6 }}>
              <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: "#52525b" }}>Units</div>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 28, lineHeight: 1 }}>{box.units_count}</div>
            </div>
          </div>

          {/* Project + Run */}
          <div style={{ marginTop: 8, fontSize: 10 }}>
            <div><b>Project:</b> {box.target_project || "—"}</div>
            <div><b>Assembly Run:</b> {box.assembly_run_number || "—"}</div>
            <div><b>Assembled:</b> {new Date(box.created_at).toLocaleDateString()}</div>
          </div>

          {/* QR — centred and large */}
          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8, borderTop: "2px solid #1e293b" }}>
            <QRCodeSVG value={traceUrl} size={170} level="M" bgColor="#fff" fgColor="#1e293b" />
            <div style={{ fontSize: 9, letterSpacing: 1, marginTop: 4 }}>Scan for full traceability</div>
            <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 13, marginTop: 2 }}>{box.box_number}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
